import { useEffect, useRef, useState, createContext, useContext, useCallback } from "react";
import { useMutation } from "convex/react";
import { convexApi } from "@/lib/convex-api";
import { useSite } from "@/lib/site-context";
import { useAuth } from "@/lib/auth-provider";
import { isNativeApp, getPlatform } from "@/lib/capacitor";
import { getCurrentBrand } from "@/lib/domain-utils";
import { toNativePath } from "@/lib/native-routes";
import type { PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { 
  checkPermissionStatus, 
  initializePushNotifications,
  cleanupPushNotificationListeners,
  getStoredFcmToken
} from "@/lib/push-notifications";

async function showLocalNotification(title: string, body: string) {
  if (!isNativeApp()) return;
  try {
    const permResult = await LocalNotifications.checkPermissions();
    if (permResult.display !== 'granted') {
      const reqResult = await LocalNotifications.requestPermissions();
      if (reqResult.display !== 'granted') return;
    }
    await LocalNotifications.schedule({
      notifications: [{
        title,
        body,
        id: Math.floor(Math.random() * 2147483647),
        schedule: { at: new Date(Date.now() + 100) },
        sound: undefined,
        smallIcon: 'ic_notification',
        largeIcon: 'ic_launcher',
      }],
    });
  } catch (e) {
    console.log('[PushNotificationManager] Local notification fallback failed:', e);
  }
}
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { UserCircle, ArrowRightLeft } from "lucide-react";

interface ForegroundNotification {
  id: string;
  title: string;
  body: string;
  timestamp: number;
  isForDifferentUser?: boolean;
  targetUserEmail?: string;
  targetUserName?: string;
}

interface AccountMismatchInfo {
  notificationTitle: string;
  notificationBody: string;
  targetUserId: string;
  targetUserEmail?: string;
  targetUserName?: string;
  targetSiteId?: string;
  targetSiteName?: string;
}

interface PushNotificationContextValue {
  foregroundNotification: ForegroundNotification | null;
  dismissNotification: () => void;
  accountMismatch: AccountMismatchInfo | null;
  dismissAccountMismatch: () => void;
}

const PushNotificationContext = createContext<PushNotificationContextValue>({
  foregroundNotification: null,
  dismissNotification: () => {},
  accountMismatch: null,
  dismissAccountMismatch: () => {},
});

export function useForegroundNotification() {
  return useContext(PushNotificationContext);
}

export function usePushNotificationContext() {
  return useContext(PushNotificationContext);
}

function extractNotificationData(data: Record<string, string | undefined> | undefined) {
  return {
    targetUserId: data?.targetUserId || null,
    targetUserEmail: data?.targetUserEmail || null,
    targetUserName: data?.targetUserName || null,
    targetSiteId: data?.targetSiteId || null,
    targetSiteName: data?.targetSiteName || null,
    route: data?.route || null,
  };
}

export function PushNotificationManager({ children }: { children?: React.ReactNode }) {
  const { currentSite: site } = useSite();
  const siteId = site?._id || null;
  const { user } = useAuth();
  const userId = user?.id || null;
  
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [foregroundNotification, setForegroundNotification] = useState<ForegroundNotification | null>(null);
  const [accountMismatch, setAccountMismatch] = useState<AccountMismatchInfo | null>(null);
  const tokenRegistrationAttempted = useRef(false);
  const initializationAttempted = useRef(false);
  
  const registerDeviceToken = useMutation(convexApi.notifications.registerDeviceToken);
  
  const dismissNotification = useCallback(() => {
    setForegroundNotification(null);
  }, []);

  const dismissAccountMismatch = useCallback(() => {
    setAccountMismatch(null);
  }, []);

  const handleNotificationTap = useCallback((action: ActionPerformed) => {
    console.log('[PushNotificationManager] Notification tapped:', action);
    const notification = action.notification;
    const data = extractNotificationData(notification.data);
    const title = notification.title || notification.data?.title || 'Notification';
    const body = notification.body || notification.data?.body || '';

    if (data.targetUserId && userId && data.targetUserId !== userId) {
      console.log('[PushNotificationManager] Notification is for a different user', {
        currentUser: userId.substring(0, 10) + '...',
        targetUser: data.targetUserId.substring(0, 10) + '...',
      });

      setAccountMismatch({
        notificationTitle: title,
        notificationBody: body,
        targetUserId: data.targetUserId,
        targetUserEmail: data.targetUserEmail || undefined,
        targetUserName: data.targetUserName || undefined,
        targetSiteId: data.targetSiteId || undefined,
        targetSiteName: data.targetSiteName || undefined,
      });
      return;
    }

    if (data.route) {
      // Payloads are written against web paths (/staff/...) but the native
      // router only knows /app/*, so unmapped routes silently landed on the
      // dashboard. toNativePath also rejects absolute URLs — this value comes
      // off the network and must not be able to navigate off-origin.
      const target = toNativePath(String(data.route));
      if (target) {
        console.log('[PushNotificationManager] Navigating to:', target, '(from', data.route + ')');
        window.location.href = target;
      } else {
        console.warn('[PushNotificationManager] Ignored unsafe route:', data.route);
      }
    }
  }, [userId]);

  const handleForegroundNotification = useCallback((notification: PushNotificationSchema) => {
    console.log('[PushNotificationManager] Foreground notification received:', notification);
    const data = extractNotificationData(notification.data);
    const title = notification.title || notification.data?.title || 'Notification';
    const body = notification.body || notification.data?.body || '';
    
    const isForDifferentUser = !!(data.targetUserId && userId && data.targetUserId !== userId);

    if (isForDifferentUser) {
      console.log('[PushNotificationManager] Foreground notification for different user', {
        currentUser: userId?.substring(0, 10) + '...',
        targetUser: data.targetUserId?.substring(0, 10) + '...',
      });
    }

    setForegroundNotification({
      id: notification.id || String(Date.now()),
      title,
      body,
      timestamp: Date.now(),
      isForDifferentUser,
      targetUserEmail: data.targetUserEmail || undefined,
      targetUserName: data.targetUserName || undefined,
    });
    setTimeout(() => {
      setForegroundNotification(prev => {
        if (prev && prev.timestamp <= Date.now() - 5500) return null;
        return prev;
      });
    }, 6000);

    showLocalNotification(title, body);
  }, [userId]);
  
  useEffect(() => {
    if (initializationAttempted.current) return;
    
    async function initPush() {
      if (!isNativeApp()) {
        console.log('[PushNotificationManager] Not a native app, skipping');
        return;
      }
      
      initializationAttempted.current = true;
      
      try {
        const status = await checkPermissionStatus();
        console.log('[PushNotificationManager] Permission status:', status);
        
        if (status === 'granted') {
          const result = await initializePushNotifications(
            (token) => {
              console.log('[PushNotificationManager] Token received via callback');
              setFcmToken(token);
            },
            handleForegroundNotification,
            handleNotificationTap
          );
          
          if (result.token) {
            console.log('[PushNotificationManager] Token from initialization:', result.token.substring(0, 20) + '...');
            setFcmToken(result.token);
          } else {
            const storedToken = getStoredFcmToken();
            if (storedToken) {
              console.log('[PushNotificationManager] Using stored token');
              setFcmToken(storedToken);
            }
          }
        } else {
          const storedToken = getStoredFcmToken();
          if (storedToken) {
            console.log('[PushNotificationManager] Using stored token (permission no longer granted)');
            setFcmToken(storedToken);
          } else {
            console.log('[PushNotificationManager] No permission, no stored token - user needs to enable in settings');
          }
        }
      } catch (error) {
        console.error('[PushNotificationManager] Failed to initialize push notifications:', error);
      }
    }
    
    initPush();
    
    return () => {
      cleanupPushNotificationListeners();
    };
  }, [handleForegroundNotification, handleNotificationTap]);
  
  useEffect(() => {
    async function registerToken() {
      if (!fcmToken || !siteId || !userId || tokenRegistrationAttempted.current) {
        return;
      }
      
      tokenRegistrationAttempted.current = true;
      
      try {
        const platform = getPlatform();
        const brand = getCurrentBrand();
        
        console.log('[PushNotificationManager] Registering device token with backend', {
          siteId,
          platform,
          brand,
          userId: userId.substring(0, 10) + '...',
          tokenPrefix: fcmToken.substring(0, 20) + '...'
        });
        
        await registerDeviceToken({
          siteId: siteId as any,
          token: fcmToken,
          platform,
          brand,
        });
        
        console.log('[PushNotificationManager] Device token registered successfully');
      } catch (error) {
        console.error('[PushNotificationManager] Failed to register device token:', error);
        tokenRegistrationAttempted.current = false;
      }
    }
    
    registerToken();
  }, [fcmToken, siteId, userId, registerDeviceToken]);
  
  // Reset registration attempt when site or user changes (re-register for new site/user)
  useEffect(() => {
    tokenRegistrationAttempted.current = false;
  }, [siteId, userId]);
  
  const contextValue = {
    foregroundNotification,
    dismissNotification,
    accountMismatch,
    dismissAccountMismatch,
  };

  return (
    <PushNotificationContext.Provider value={contextValue}>
      {children}
      <AccountMismatchDialog
        mismatch={accountMismatch}
        onDismiss={dismissAccountMismatch}
      />
    </PushNotificationContext.Provider>
  );
}

function AccountMismatchDialog({ 
  mismatch,
  onDismiss,
}: { 
  mismatch: AccountMismatchInfo | null;
  onDismiss: () => void;
}) {
  const { signOut } = useAuth();

  if (!mismatch) return null;

  const displayName = mismatch.targetUserName || mismatch.targetUserEmail || 'a different account';
  const siteName = mismatch.targetSiteName;

  const handleSwitchAccount = async () => {
    onDismiss();
    await signOut();
  };

  return (
    <AlertDialog open={!!mismatch} onOpenChange={(open) => { if (!open) onDismiss(); }}>
      <AlertDialogContent className="max-w-sm mx-4" data-testid="dialog-account-mismatch">
        <AlertDialogHeader>
          <div className="flex justify-center mb-3">
            <div className="rounded-full bg-muted p-3">
              <UserCircle className="h-8 w-8 text-muted-foreground" />
            </div>
          </div>
          <AlertDialogTitle className="text-center text-lg">
            Different Account
          </AlertDialogTitle>
          <AlertDialogDescription className="text-center space-y-3">
            <span className="block">
              This notification is for <span className="font-semibold text-foreground">{displayName}</span>
              {siteName && (
                <span> at <span className="font-semibold text-foreground">{siteName}</span></span>
              )}
            </span>
            <span className="block text-sm">
              &ldquo;{mismatch.notificationTitle}&rdquo;
            </span>
            <span className="block text-sm">
              Please sign in with that account to view this notification.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col gap-2 sm:flex-col">
          <Button 
            onClick={handleSwitchAccount}
            className="w-full gap-2"
            data-testid="button-switch-account"
          >
            <ArrowRightLeft className="h-4 w-4" />
            Switch Account
          </Button>
          <Button 
            variant="ghost"
            onClick={onDismiss}
            className="w-full"
            data-testid="button-dismiss-mismatch"
          >
            Dismiss
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
